






























































import { Component, Ref, Vue } from "vue-property-decorator";
import router from "@/router/router";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import TextInputDialogComponent from "@/components/common/TextInputDialog.vue";
import ContentStateComponent from "@/components/common/ContentState.vue";
import BeschreibungSectionsComponent from "@/components/admin/infoConfig/BeschreibungSections.vue";
import BeschreibungRowDialogComponent from "@/components/admin/infoConfig/BeschreibungRowDialog.vue";
import InfoConfigPublishDialogComponent from "@/components/admin/infoConfig/InfoConfigPublishDialog.vue";
import { listInfoConfigVersions } from "@/data/repository/InfoConfigAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import {
  BeschreibungDraft,
  BeschreibungFile,
  BeschreibungRow,
  beschreibungText,
  cellValue,
  clearDraft,
  encodeBeschreibung,
  keyExists,
  parseBeschreibung,
  readDraft,
  withCell,
  withRowInserted,
  withRowRemoved,
  todayLabel,
  withVersionHeader,
  writeDraft,
} from "@/utils/BeschreibungFile";
import { infoConfigErrorKey, VERSION_PATTERN } from "@/utils/InfoConfigAdministration";
import { COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { ComponentName } from "@/utils/RouteGuards";
import { themed } from "@/utils/Theme";

/**
 * The draft of a new Beschreibung version, made from a published one (decisions were made on
 * 2026-10-05 (Taimoor)): cells edited row by row, rows added after another or removed, inside existing
 * sections. The draft lives in this browser and is saved on every change, so a reload keeps it; nothing
 * reaches IRES Cloud until PRÜFEN UND VERÖFFENTLICHEN, where IRES Cloud checks it like an upload. The
 * published versions themselves are never edited. Only a named SysAdmin gets here from a version's page.
 */
@Component({
  components: {
    "beschreibung-sections": BeschreibungSectionsComponent,
    "row-dialog": BeschreibungRowDialogComponent,
    "publish-dialog": InfoConfigPublishDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
    "text-input-dialog": TextInputDialogComponent,
    "content-state": ContentStateComponent,
  },
})
export default class BeschreibungDraftEditorComponent extends Vue {
  @Ref("rowDialog")
  rowDialog!: BeschreibungRowDialogComponent;

  @Ref("publishDialog")
  publishDialog!: InfoConfigPublishDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  @Ref("keyDialog")
  keyDialog!: TextInputDialogComponent;

  draft: BeschreibungDraft | null = null;
  file: BeschreibungFile | null = null;
  search = "";
  /** False when the browser refused to keep the draft (private mode, full storage). */
  saved = true;

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  /** Keeps a changed file as the draft. */
  update(file: BeschreibungFile) {
    if (!this.draft) {
      return;
    }
    this.file = file;
    this.draft = { ...this.draft, text: beschreibungText(file), savedMillis: Date.now() };
    this.saved = writeDraft(window.localStorage, this.draft);
    this.updateToolbarTitle();
  }

  /** "Beschreibungen > Entwurf 5.58 (aus 5.57)": the draft is named in the app bar only. */
  updateToolbarTitle() {
    const base = this.$t("admin.infoConfig.title").toString();
    ToolbarContentModule.setToolbarTitle(
      this.draft ? `${base} > ${this.$t("admin.infoConfig.draft.title", [this.draft.version, this.draft.baseVersion])}` : base
    );
  }

  async onEditRow(row: BeschreibungRow) {
    if (!this.file) {
      return;
    }
    const changes = await this.rowDialog.show(this.file, row);
    if (!changes) {
      return;
    }
    // Only cells whose value really changed: an untouched cell is never rewritten.
    let file = this.file;
    for (const change of changes) {
      if (cellValue(file, row.lineIndex, change.cell) !== change.value) {
        file = withCell(file, row.lineIndex, change.cell, change.value);
      }
    }
    this.update(file);
  }

  async onInsertRow(row: BeschreibungRow) {
    if (!this.file) {
      return;
    }
    const file = this.file;
    const key = await this.keyDialog.show(
      "",
      this.$t("admin.infoConfig.draft.insertRow").toString(),
      this.$t("admin.infoConfig.draft.insertRowMessage", [row.key]).toString(),
      this.$t("admin.infoConfig.sections.columns.key").toString(),
      (value: string) =>
        value.trim() === ""
          ? this.$t("admin.infoConfig.draft.keyRequired").toString()
          : keyExists(file, value.trim())
          ? this.$t("admin.infoConfig.draft.keyTaken").toString()
          : null
    );
    if (typeof key === "string" && key.trim() !== "") {
      this.update(withRowInserted(file, row.lineIndex, key.trim()));
    }
  }

  async onRemoveRow(row: BeschreibungRow) {
    if (!this.file) {
      return;
    }
    const confirmed = await this.confirmDialog.show({
      title: this.$t("admin.infoConfig.draft.removeRow").toString(),
      message: this.$t("admin.infoConfig.draft.removeRowMessage", [row.key]).toString(),
      acceptMessage: this.$t("admin.infoConfig.draft.removeRow").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (confirmed) {
      this.update(withRowRemoved(this.file, row.lineIndex));
    }
  }

  /** Another number for the draft, its `#Version` line following; IRES Cloud checks the number on publish. */
  async onChangeVersion() {
    if (!this.draft || !this.file) {
      return;
    }
    const version = await this.keyDialog.show(
      this.draft.version,
      this.$t("admin.infoConfig.draft.changeVersion").toString(),
      "",
      this.$t("admin.infoConfig.publish.version").toString(),
      (value: string) => (VERSION_PATTERN.test(value.trim()) ? null : this.$t("admin.infoConfig.publish.versionProblems.FORMAT").toString())
    );
    if (typeof version === "string" && VERSION_PATTERN.test(version.trim())) {
      this.draft = { ...this.draft, version: version.trim() };
      this.update(withVersionHeader(this.file, version.trim(), todayLabel()));
    }
  }

  async onCheckAndPublish() {
    if (!this.draft || !this.file) {
      return;
    }
    let rows;
    try {
      rows = (await listInfoConfigVersions()).room;
    } catch (error) {
      SnackbarModule.showError({ key: infoConfigErrorKey(error) });
      return;
    }
    const published = await this.publishDialog.show(rows, {
      contentBase64: encodeBeschreibung(beschreibungText(this.file), this.draft.bom),
      version: this.draft.version,
      label: this.$t("admin.infoConfig.draft.title", [this.draft.version, this.draft.baseVersion]).toString(),
    });
    if (published) {
      clearDraft(window.localStorage);
      router.push({ name: ComponentName.INFO_CONFIG_VERSION, query: { kind: "ROOM", version: published } });
    }
  }

  async onDiscard() {
    const confirmed = await this.confirmDialog.show({
      title: this.$t("admin.infoConfig.draft.discardTitle").toString(),
      message: this.$t("admin.infoConfig.draft.discardMessage").toString(),
      acceptMessage: this.$t("admin.infoConfig.draft.discard").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (confirmed) {
      clearDraft(window.localStorage);
      this.onBack();
    }
  }

  onBack() {
    router.push({ name: ComponentName.INFO_CONFIG_LIST });
  }

  mounted() {
    this.draft = readDraft(window.localStorage);
    this.file = this.draft ? parseBeschreibung(this.draft.text, this.draft.bom) : null;
    this.updateToolbarTitle();
  }
}
